import { h } from "preact";
import { createPortal } from "preact/compat";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import sortMaterials from "../../helpers/sortMaterials/sortMaterials";
import useSwatchDetails from "../../helpers/useSwatchDetails/useSwatchDetails";
import useModalOpenState from "./useModalOpenState";
import useSwatchBasket from "./useSwatchBasket";
import SwatchDetailsSummary from "./swatch-details-summary";
import SwatchDetailsTabs, { TAB_ALL } from "./swatch-details-tabs";
import SwatchDetailsGrid from "./swatch-details-grid";
import SwatchDetailsFooter from "./swatch-details-footer";

export const SWATCH_DETAILS_MODAL_ID = "swatch-details-modal";

export default function SwatchDetailsDrawer({
  items,
  optionName,
  productTitle,
  idPrefix,
  variantImage,
  updateOptionValue,
}) {
  const open = useModalOpenState(SWATCH_DETAILS_MODAL_ID);
  const [activeTab, setActiveTab] = useState(TAB_ALL);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const basket = useSwatchBasket({ items, open, productTitle });
  const scrollRef = useRef(null);

  // createPortal appends rather than claiming the container, so a second
  // instance would stack a second drawer. ProductOptions renders this only for
  // the one <product-form-options> carrying enable-swatch-preview.
  const mount = useMemo(
    () =>
      document.querySelector(
        `#${SWATCH_DETAILS_MODAL_ID} [data-swatch-details-mount]`,
      ),
    [],
  );

  const selectedItem = items.find(({ selected }) => selected) || null;

  const [details, detailsLoading] = useSwatchDetails(
    open ? selectedItem?.swatchProduct?.gid || null : null,
  );

  const materialGroups = useMemo(
    () =>
      [
        ...new Set(
          items.map(({ materialGroup }) => materialGroup).filter(Boolean),
        ),
      ].sort(sortMaterials),
    [items],
  );

  useEffect(() => {
    if (!open) return;

    setActiveTab(TAB_ALL);
    setError(null);
    // markup-modal resets [data-inner-wrapper] on close, but the scrolling box
    // here is further in.
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [open]);

  const visibleItems =
    activeTab === TAB_ALL
      ? items
      : items.filter(({ materialGroup }) => materialGroup === activeTab);

  const handleSubmit = () => {
    setSubmitting(true);
    setError(null);

    basket
      .submit()
      .then(() => {
        // The closed event can't say which modal closed, so don't wait on it.
        document.dispatchEvent(new CustomEvent("markup-modal:close"));
        document.dispatchEvent(new CustomEvent("mini-cart:open"));
      })
      .catch(() => setError(window.localeStrings.product.add_to_cart_error))
      .finally(() => setSubmitting(false));
  };

  if (!mount) return null;

  const strings = window.localeStrings.swatch_details;

  return createPortal(
    <div className="swatch-details-drawer">
      <div className="swatch-details-drawer__header">
        <h2 className="swatch-details-drawer__title">{strings.title}</h2>
        <p className="swatch-details-drawer__subtitle">
          {basket.capped
            ? strings.subtitle.replace("[total]", basket.totalCap)
            : strings.subtitle_uncapped}
        </p>
      </div>
      <div className="swatch-details-drawer__scroll" ref={scrollRef}>
        <SwatchDetailsSummary
          item={selectedItem}
          variantImage={variantImage}
          details={details}
          detailsLoading={detailsLoading}
        />
        <div className="swatch-details-drawer__catalogue-header">
          <h3 className="swatch-details-drawer__catalogue-title">
            {strings.other_colors}
          </h3>
          {materialGroups.length > 0 && (
            <SwatchDetailsTabs
              name={`${idPrefix}swatch-details-tab`}
              groups={materialGroups}
              value={activeTab}
              onChange={setActiveTab}
            />
          )}
        </div>
        <SwatchDetailsGrid
          items={visibleItems}
          optionName={optionName}
          radioName={`${idPrefix}swatch-details-${optionName}`}
          basket={basket.basket}
          limitEach={basket.limitEach}
          canAddMore={basket.canAddMore}
          onSelect={updateOptionValue}
          onToggle={basket.toggle}
          onQuantityChange={basket.setQuantity}
        />
      </div>
      <SwatchDetailsFooter
        basket={basket.basket}
        swatchCatalogue={basket.swatchCatalogue}
        basketCount={basket.basketCount}
        canAddMore={basket.canAddMore}
        hasChanges={basket.hasChanges}
        submitting={submitting}
        error={error}
        onRemove={(variantId) => basket.setQuantity(variantId, 0)}
        onSubmit={handleSubmit}
      />
    </div>,
    mount,
  );
}
